/**
 * DIÁLOGO DO e-TORNEIOS PARA CONFIRMAR / PEDIR MOTIVO (Fase 2 — item 7).
 *
 * Substitui as janelinhas cinzas do navegador (`window.prompt`) por um diálogo
 * com a cara do sistema. Nasceu de um problema real: ao recusar um documento,
 * o motivo (que a regra pede) era digitado numa caixinha de UMA linha, sem
 * contexto nenhum — fácil de perder e de errar.
 *
 * Como foi feito para NÃO mudar comportamento:
 *   * o valor devolvido é o texto digitado (ou '' se vazio), igual ao prompt;
 *   * quando o fluxo antigo tratava "cancelar" como desistir, `aoCancelar` é
 *     chamado e NADA acontece (o mesmo `return` de antes);
 *   * quando o fluxo antigo tratava qualquer fechamento como confirmar (caso da
 *     rejeição de atleta, que usava `prompt(...) || ''`), use `soConfirmar`:
 *     o diálogo mostra UM botão e qualquer forma de fechar confirma, exatamente
 *     como o prompt fazia.
 *
 * Usa o `ui/dialog` que já existe (não o modifica) — Radix cuida de foco, Esc e
 * leitores de tela, o que o prompt nativo não fazia.
 */
import { useEffect, useRef, useState } from 'react';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';

export default function DialogoMotivo({
  aberto,
  titulo,
  descricao,
  rotulo = 'Motivo (opcional)',
  placeholder = 'Escreva aqui...',
  textoConfirmar = 'Confirmar',
  varianteConfirmar = 'default',
  soConfirmar = false,
  aoConfirmar,
  aoCancelar,
  testId,
}) {
  const [texto, setTexto] = useState('');
  const confirmou = useRef(false);
  const idCampo = testId ? `${testId}-texto` : 'dialogo-motivo-texto';

  // Cada abertura começa vazia — igual ao `prompt(msg, '')`.
  useEffect(() => {
    if (aberto) {
      setTexto('');
      confirmou.current = false;
    }
  }, [aberto]);

  // Trava contra confirmação dupla: quando o diálogo fecha por causa da própria
  // confirmação, o Radix avisa "fechou" — sem esta trava o fluxo `soConfirmar`
  // (rejeição de atleta) chamaria a ação duas vezes.
  const confirmar = () => {
    if (confirmou.current) return;
    confirmou.current = true;
    aoConfirmar(texto);
  };

  return (
    <Dialog
      open={aberto}
      onOpenChange={(v) => {
        if (v) return;
        // Fechou (Esc, X ou clique fora): se o fluxo antigo confirmava mesmo
        // assim, confirmamos; senão é cancelamento, como antes.
        if (soConfirmar) confirmar();
        else if (aoCancelar) aoCancelar();
      }}
    >
      <DialogContent className="bg-[#141414] border-[#27272A]" data-testid={testId}>
        <DialogHeader>
          <DialogTitle className="text-white">{titulo}</DialogTitle>
          {descricao && <DialogDescription className="text-[#A1A1AA]">{descricao}</DialogDescription>}
        </DialogHeader>

        <div className="space-y-1.5">
          <Label htmlFor={idCampo} className="text-[#A1A1AA] text-xs">{rotulo}</Label>
          <Textarea
            id={idCampo}
            value={texto}
            onChange={(e) => setTexto(e.target.value)}
            placeholder={placeholder}
            rows={3}
            data-testid={testId ? `${testId}-campo` : undefined}
          />
        </div>

        <DialogFooter className="gap-2">
          {!soConfirmar && (
            <Button
              type="button"
              variant="outline"
              onClick={() => (aoCancelar ? aoCancelar() : undefined)}
              data-testid={testId ? `${testId}-cancelar` : undefined}
            >
              Cancelar
            </Button>
          )}
          <Button
            type="button"
            variant={varianteConfirmar}
            onClick={confirmar}
            data-testid={testId ? `${testId}-confirmar` : undefined}
          >
            {textoConfirmar}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
